/**
 * The FAQ content shown on /faq — and the single source for the FAQPage
 * JSON-LD that prerender.mjs bakes into that page.
 *
 * Google requires FAQPage markup to match the answer text a visitor actually
 * sees. Keeping one array means the schema cannot drift from the accordion;
 * editing an answer here updates both on the next build.
 *
 * Answers are deliberately literal about how the tool behaves. Do not add
 * claims the code does not support — check src/ before writing one. Spins use
 * crypto.getRandomValues (src/utils/random.ts); Math.random survives only as a
 * fallback for environments without the Web Crypto API, and for cosmetic things
 * like confetti scatter.
 */
export interface Faq {
  q: string;
  a: string;
}

export interface FaqGroup {
  label: string;
  items: Faq[];
}

/**
 * The questions the homepage shows — and the exact set its FAQPage declares.
 *
 * The homepage used to render the first few entries of the full array, which
 * made /faq almost entirely a copy of it — a duplicate-content audit measured
 * only one word on /faq that did not also appear on the homepage. The homepage
 * now has its own short, search-intent-led set (HOME_FAQS below) and links
 * through to /faq for the detailed answers, which keeps /faq's text its own.
 *
 * Read by BOTH the accordion and prerender.mjs, because Google requires FAQPage
 * markup to match the answers actually rendered. If the homepage declared more
 * than it shows, the markup would describe content that is not on the page.
 */
export const HOME_FAQS: Faq[] = [
  {
    q: 'What is a random wheel?',
    a: 'A random wheel is a digital spinning wheel that randomly selects one option from a list you provide. Use it to pick names, make decisions, choose winners, create teams, or settle any choice fairly.',
  },
  {
    q: 'Is TheNameWheel free to use?',
    a: 'Yes. TheNameWheel is completely free. No account or signup is required. Open the site, add your options, and spin.',
  },
  {
    q: 'How do I pick a random name online?',
    a: 'Type or paste your names into the wheel, then click Spin. The wheel randomly selects one name each time you spin.',
  },
  {
    q: 'Can I use this for classroom activities?',
    a: 'Yes. Teachers use TheNameWheel to call on students, form random groups, assign tasks, and run classroom games fairly.',
  },
  {
    q: 'How do I pick a random winner for a giveaway?',
    a: 'Add your participants\' names to the wheel and spin. The wheel randomly selects one winner. Spin again to pick a runner-up.',
  },
  {
    q: 'Does TheNameWheel work on mobile?',
    a: 'Yes. TheNameWheel is designed for desktop, tablet, and mobile. No app download is required.',
  },
  {
    q: 'Are my entries private?',
    a: 'Entries are stored in this browser by default and no account is required. Share, Embed, and QR links are different: they encode your wheel data in the URL, so avoid sensitive information and share those links only with the intended people.',
  },
  {
    q: 'Can I save my wheel for later?',
    a: 'Yes. Click Saved, open the Save tab, name the wheel, and choose Save Wheel. You can keep up to 20 named wheels in this browser; clearing its site data removes them. Use Share or Export when you need to move a wheel to another device.',
  },
];

export const HOME_FAQ_COUNT = HOME_FAQS.length;

// ---------------------------------------------------------------------------
// Grouped FAQ data — the authoritative structure.
// ---------------------------------------------------------------------------

export const FAQ_GROUPS: FaqGroup[] = [
  // ── Getting started ─────────────────────────────────────────────────────
  {
    label: 'Getting started',
    items: [
      {
        q: 'How does a random name picker work?',
        a: 'Enter names or items, then click SPIN or the wheel itself. The picker chooses an entry before the animation begins and animates the wheel to reveal it. Equal-weight entries have equal odds; duplicate entries and custom weights deliberately change those odds. Auto-Remove can remove the selected entry after the result.',
      },
      {
        q: 'What random source does the picker use?',
        a: 'In supported browsers, selection uses crypto.getRandomValues with rejection sampling. Older environments fall back to Math.random. In both cases the entry is selected before the animation starts, so spin duration and how long you watch do not determine the result.',
      },
      {
        q: 'How do I add multiple names at once?',
        a: 'Switch to the "Bulk Edit" tab in the entries panel. You can paste or type a list of names — one per line — and the wheel updates instantly. This is the fastest way to load a large list of participants.',
      },
      {
        q: 'Can I remove the winner automatically after each spin?',
        a: 'Yes. Open Options and enable Auto-Remove Winner. The selected entry is removed after the result, so that entry cannot be picked again. If one person appears in multiple entries, remove their remaining entries too when your rules prohibit repeat winners.',
      },
      {
        q: 'Is TheNameWheel free to use?',
        a: 'Yes, the core wheel is free and requires no account. Entries are processed in your browser, while the site uses limited consent-mode analytics and consent-based advertising as described in the privacy policy.',
      },
    ],
  },

  // ── Entries and lists ───────────────────────────────────────────────────
  {
    label: 'Entries and lists',
    items: [
      {
        q: 'What is the maximum number of entries?',
        a: 'Up to 50,000. Above about 240 entries the wheel starts showing a sample of them, because a wheel only has so much rim: at 50,000 slices each one would be a thirtieth of a pixel wide, which no screen can draw. The draw itself always runs across every entry you have added, and the wheel stops on whichever one wins — so a 50,000-name raffle really is 1-in-50,000. The wheel says "showing 240 of 50,000" whenever it is sampling, so you are never guessing. Note that share links and QR codes cannot carry a list that large; use Export to send a big wheel as a file.',
      },
      {
        q: 'How do I import a CSV file of names?',
        a: 'Use the Import button in the entries panel. Your CSV must have one name per column or one per row; the first column or row is used if multiple exist. The names are added to the wheel immediately.',
      },
      {
        q: 'Can I have duplicate names on the wheel?',
        a: 'Yes — adding the same name twice doubles its probability, which is equivalent to setting its weight to 2. This is a simple way to give one entry more chances without opening the Weights panel.',
      },
      {
        q: 'Is there a minimum number of entries?',
        a: 'You need at least 2 entries to spin. The wheel requires two or more segments to produce a meaningful random pick.',
      },
      {
        q: 'How do I export my entries as a file?',
        a: 'Click the Export button in the entries panel. It downloads a .txt file with one name per line. You can re-import this file on any device to restore your wheel.',
      },
      {
        q: 'Can I reorder entries manually?',
        a: 'Drag to reorder is not supported. Use Shuffle for a random order, or Sort A–Z to arrange entries alphabetically. The order affects only which segment sits where on the rim — the probabilities stay the same.',
      },
    ],
  },

  // ── Weighted entries ────────────────────────────────────────────────────
  {
    label: 'Weighted entries',
    items: [
      {
        q: 'What are weights and how do they work?',
        a: 'A weight multiplies an entry\'s probability. A weight of 3 means that name is 3× as likely to be picked as an entry with weight 1. The wheel calculates the total weight of all entries and assigns each one a proportional chance.',
      },
      {
        q: 'Can I set different weights for different entries?',
        a: 'Yes — open the Weights panel and set a numeric weight per entry. Entries without a custom weight default to 1. This lets you run draws where some participants have higher or lower odds.',
      },
      {
        q: 'Do weights change how the wheel looks?',
        a: 'Yes — a weighted entry gets a proportionally larger segment on the wheel, reflecting its higher probability. You can toggle this in Options: "Weighted Segments" makes slices proportional, while the alternative draws equal slices but keeps the weighted odds.',
      },
      {
        q: 'Can I use decimal weights like 0.5?',
        a: 'Yes — any positive number is valid. A weight of 0.5 gives an entry half the probability of a weight-1 entry. This is useful when you want to reduce someone\'s chances rather than increase everyone else\'s.',
      },
    ],
  },

  // ── Sharing and embedding ───────────────────────────────────────────────
  {
    label: 'Sharing and embedding',
    items: [
      {
        q: 'Can I save and share my wheel?',
        a: 'Yes. For a reusable local copy, click Saved, open the Save tab, name the wheel, and choose Save Wheel; named wheels stay in that browser and the limit is 20. Share creates a URL containing supported wheel data for smaller wheels, while Export downloads the entry list for moving a large wheel to another device.',
      },
      {
        q: 'Does it work on mobile and tablet?',
        a: 'Absolutely. The wheel is fully touch-friendly. You can tap the wheel or the Spin button on any device. The layout automatically adapts for smaller screens, stacking the wheel and entries panel vertically.',
      },
      {
        q: 'Can I change the colors of the wheel?',
        a: 'Yes, two ways. Pick a palette in Options — Classic, Pastel, Corporate, Sunset, Forest or Slate — and every segment recolours at once. Or set a single entry\'s colour directly: each row in the entry list has a colour dot that opens your browser\'s colour picker, so you can match brand colours for a prize wheel or event. Entries you do not set individually keep the palette colour for their position, which is what guarantees neighbouring segments never share a shade. Custom colours travel in share links and saved wheels.',
      },
      {
        q: 'Can I use pre-made wheels from the Gallery?',
        a: 'Yes! Visit the Gallery page to browse ready-made wheels for common scenarios — days of the week, dice, movie genres, countries, and more. Click any template to load it instantly into the spinner.',
      },
      {
        q: 'How long is a shared wheel link valid?',
        a: 'The app does not assign an expiry date because the wheel data is encoded in the URL rather than stored as a server-side record. The link remains usable while you retain the complete URL and the site continues to support that share format, so keep an exported copy for long-term records.',
      },
      {
        q: 'Can I share a wheel with 50,000 names?',
        a: 'Share links have a URL length limit — very large lists should be exported as a file and shared separately. The QR code has the same limitation. For wheels with more than a few hundred entries, use the Export button to send the list as a .txt file.',
      },
      {
        q: 'How do I embed the wheel on my school\'s website or LMS?',
        a: 'Open your wheel, click Embed, and copy the generated iframe code into a page or LMS block that permits iframe HTML. The full embedded workspace lets visitors edit entries and options, add images, spin, and review results without leaving the host page. Platform policies vary, so test the published page.',
      },
      {
        q: 'Can students add themselves to an embedded wheel?',
        a: 'The full-app iframe includes the entry editor, so a visitor can add or remove entries inside their own browser session. If you need a spin-focused wheel without the full editor, use the compact /embed/ endpoint described in the embed guide.',
      },
      {
        q: 'What are the embed customization options?',
        a: 'The generated iframe carries the wheel\'s current palette, rim, font, font size, spin duration, weighted-slice setting, Auto-Remove setting, sound, confetti, and winner display. The host controls the iframe width and height, and visitors can use the normal editor controls inside the full-app embed.',
      },
    ],
  },

  // ── Linked wheels ───────────────────────────────────────────────────────
  {
    label: 'Linked wheels',
    items: [
      {
        q: 'What are linked wheels?',
        a: 'A chain of wheels where the result of one spin determines the entries on the next — for example, spin a subject wheel, and the following topic wheel only shows topics for that subject. This lets you build multi-stage random selections.',
      },
      {
        q: 'How do I set up a conditional linked wheel?',
        a: 'On the Linked Wheels page, add a wheel, then add a second wheel and set its entries to "conditional". Map each possible result from wheel 1 to a different entry list for wheel 2. When you spin wheel 1, wheel 2 loads only the entries you mapped to that result.',
      },
      {
        q: 'Can I share a linked wheel chain?',
        a: 'Yes — use the Share button on the Linked Wheels page to get a URL that encodes the full chain configuration. Anyone who opens the link will see the complete multi-wheel setup, ready to spin through.',
      },
      {
        q: 'Is linked wheel history saved?',
        a: 'Completed chains are saved in this browser only, via localStorage. Use CSV export to keep a permanent record. Clearing your browser data removes the history.',
      },
    ],
  },

  // ── Privacy and compliance ──────────────────────────────────────────────
  {
    label: 'Privacy and compliance',
    items: [
      {
        q: 'Is this tool COPPA compliant?',
        a: 'We do not claim COPPA certification or compliance. Entries are stored in your browser by default, while shared URLs can expose them and the site uses limited consent-mode analytics plus consent-based advertising. Review the privacy policy and ask your school or district to make the formal determination.',
      },
      {
        q: 'Does the site use cookies?',
        a: 'Functional preferences use localStorage. AdSense does not load until you accept advertising, and GA4 may set analytics cookies only after acceptance. Before a choice or after Essential Only, GA4 can still send limited consent-denied measurement requests without analytics-storage permission; see the privacy policy for details.',
      },
      {
        q: 'Can I request deletion of my data?',
        a: 'Your wheel entries are stored in your browser by default. Clear this site\'s local storage in browser settings to delete that copy. Share, Embed, and QR links are different: they encode the title and entries in the URL, so recipients and services handling that link can receive them.',
      },
      {
        q: 'Is TheNameWheel safe to use?',
        a: 'It can be used without an account, and wheel entries stay in local browser storage by default. Local storage is not encryption, and Share, Embed, and QR URLs contain the wheel data. Do not enter sensitive information, review links before posting them, and use the privacy controls appropriate for your setting.',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Flat array — backwards-compatible with every existing consumer.
//
// The homepage teaser, prerender.mjs, and entry-server.tsx all read FAQS as a
// flat Faq[]. Deriving it from FAQ_GROUPS means the two can never disagree.
// ---------------------------------------------------------------------------

export const FAQS: Faq[] = FAQ_GROUPS.flatMap(g => g.items);

/** The set the homepage renders — and the exact set its FAQPage declares. */
export function homeFaqs(): Faq[] {
  return HOME_FAQS;
}
